import { useFolderDragHandlers } from './useFolderDragHandlers'
import { useSelectionStore } from '@stores/useSelectionStore'
import { FileExplorerItem, DraggedItem, DragDropHandlers } from '@features/file-explorer/types'

interface UseItemDragProps {
  item: FileExplorerItem
  items: FileExplorerItem[]
  getItemKey: (item: FileExplorerItem) => string
  dragAndDrop: DragDropHandlers
}

export function useItemDrag({ item, items, getItemKey, dragAndDrop }: UseItemDragProps) {
  // Drag handlers for folders
  const folderDragHandlers = useFolderDragHandlers({
    folderId: item.id,
    dragDropHandlers: dragAndDrop,
    enabled: item.is_folder
  })

  // Handle drag start for moving items
  const handleDragStart = (e: React.DragEvent) => {
    // Check if this item is in the current selection
    const selectedKeys = useSelectionStore.getState().selectedItems
    const thisItemKey = getItemKey(item)
    const isItemSelected = selectedKeys.has(thisItemKey)

    // If item is selected, drag all selected items
    if (isItemSelected && selectedKeys.size > 1) {
      const selectedItemsData: DraggedItem[] = items
        .filter(i => selectedKeys.has(getItemKey(i)))
        .map(i => ({
          type: i.type as 'Document' | 'Folder',
          id: i.id,
          name: i.name,
          currentFolderId: i.is_folder ? undefined : i.parent_id,
          currentParentId: i.is_folder ? i.parent_id : undefined
        }))

      dragAndDrop.handleItemDragStart?.(selectedItemsData)
    } else {
      // Drag only this item
      const draggedItemData: DraggedItem = {
        type: item.type as 'Document' | 'Folder',
        id: item.id,
        name: item.name,
        currentFolderId: item.is_folder ? undefined : item.parent_id,
        currentParentId: item.is_folder ? item.parent_id : undefined
      }
      dragAndDrop.handleItemDragStart?.(draggedItemData)
    }

    // Set drag effect
    e.dataTransfer.effectAllowed = 'move'
  }

  const isDraggingThisItem = dragAndDrop.draggedItem?.id === item.id && dragAndDrop.draggedItem?.type === item.type

  // Check if this item is part of a multi-item drag
  const isPartOfDrag = dragAndDrop.draggedItems?.some(
    (draggedItem) => draggedItem.id === item.id && draggedItem.type === item.type
  ) ?? false

  const dragCount = dragAndDrop.draggedItems?.length ?? 0

  return {
    handleDragStart,
    isDraggingThisItem,
    isPartOfDrag,
    dragCount,
    folderDragHandlers
  }
}
